import React, { useCallback, useMemo } from 'react'

import { Button } from '@refera/ui-web'
import { navigate, useLocation, useParams } from '@reach/router'
import useRolePermission from '_/hooks/use-role-permission'
import { parse } from 'query-string'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'
import { getStepStatusLog, updateBudget, updateBudgetResolution } from '_/modules/budget/actions'
import { useDispatch } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { ROUTES } from '_/utils/constants'
import { getServiceOrder } from '_/modules/service-orders/actions'
import { verifyIfBudgetIsSent } from '_/utils/verify-if-budget-is-sent'
import { BUTTONS_ACTION } from '_/utils/constants/service-order'
import { CanDoButtonAction } from '_/utils/can-do-button-action'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { useFormContext } from 'react-hook-form'
import {
  createFormDataBudgetResolution,
  sanitizeResolution,
} from '_/views/budget-view-v2/utils/functions'

export default function SendButton() {
  const dispatch = useDispatch()
  const { serviceOrderId, budgetId } = useParams()
  const { isTradesman, isSAAS, checkIfUserDoesNotPermission } = useRolePermission()
  const { pathname, search } = useLocation()
  const { tipo: budgetType } = parse(search)
  const { isEditing, setIsEditing, setLoading, budget, setResetForm, fetchBudget } = useBudgetForm()
  const { getValues, handleSubmit } = useFormContext()
  const { showToast } = useToast()

  const isCreating = pathname.includes('/novo')
  const isBudgetSent = verifyIfBudgetIsSent(budget)

  const canProviderEditBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_BUDGET_BY_PROVIDER,
  })

  const canSAASEditBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_BUDGET,
  })

  const isValidSAASBudgetType =
    budgetType === 'externo' || (budget && budget?.proposeOpened === false)

  const showButton = useMemo(() => {
    return (
      isEditing &&
      Number(budgetId) === Number(budget?.id) &&
      !isCreating &&
      (isTradesman || (isSAAS && isValidSAASBudgetType))
    )
  }, [isCreating, isTradesman, isSAAS, isValidSAASBudgetType, isEditing, budget, budgetId])

  const updateBudgetResolutions = useCallback(
    async budgetResolutions => {
      try {
        const promises = budgetResolutions.map(resolution => {
          const clean = sanitizeResolution(resolution)
          const formData = createFormDataBudgetResolution(clean)

          return dispatch(updateBudgetResolution(budgetId, formData))
        })

        await Promise.all(promises)
      } catch (error) {
        showToast({
          type: 'error',
          message: formatErrorMessage(error),
        })
        setLoading(false)
        throw error
      }
    },
    [dispatch, budgetId, showToast, setLoading]
  )

  const handleSave = useCallback(async () => {
    if (!budget || !(budget?.budgetResolution?.length > 0)) {
      showToast({
        type: 'error',
        message: 'Para enviar o orçamento, é necessário cadastrar pelo menos um serviço',
      })
      return
    }

    if ((isTradesman && !canProviderEditBudget) || (isSAAS && !canSAASEditBudget)) {
      showToast({
        type: 'error',
        message: 'Este chamado está em uma etapa que não permite incluir orçamento.',
      })
      return
    }

    const permission = isBudgetSent ? PERMISSIONS.BUDGETS : PERMISSIONS.ORIGINAL_BUDGETS
    const permissionAction = PERMISSIONS_ACTIONS.EDIT
    const unauthorized = checkIfUserDoesNotPermission(permission, permissionAction)

    if (unauthorized) return

    setLoading(true)
    const payload = getValues()
    // removing fields that are not needed in the payload
    const { budgetResolutions, ...payloadToSend } = payload

    const options = { transformPayload: true, transformFormData: true, formData: true }

    if (!isBudgetSent) {
      payloadToSend.released = 'true'
    }

    payloadToSend.newBudgetResolutionsOrder = budgetResolutions.map(({ id }) => id).join(',')
    payloadToSend.visitHappenedTemp = String(payloadToSend.visitHappenedTemp)

    if (budgetResolutions?.length > 0) {
      try {
        await updateBudgetResolutions(budgetResolutions)
      } catch (error) {
        return
      }
    }

    dispatch(updateBudget(serviceOrderId, budgetId, payloadToSend, options))
      .then(newBudget => {
        // redirecting to clone budget
        setIsEditing(false)
        fetchBudget(newBudget?.id)
        dispatch(getServiceOrder(serviceOrderId))
        dispatch(getStepStatusLog(serviceOrderId))

        if (newBudget?.id && !isBudgetSent) {
          setResetForm(true)
          navigate(`${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${newBudget?.id}`)
        }

        showToast({ type: 'success' })
      })
      .catch(error =>
        showToast({
          message: formatErrorMessage(error),
          type: 'error',
        })
      )
      .finally(() => setLoading(false))
  }, [
    serviceOrderId,
    budgetId,
    dispatch,
    setLoading,
    showToast,
    budget,
    setResetForm,
    fetchBudget,
    setIsEditing,
    isBudgetSent,
    canProviderEditBudget,
    canSAASEditBudget,
    isTradesman,
    isSAAS,
  ])

  const label = isBudgetSent ? 'Salvar' : 'Enviar'

  return (
    showButton && (
      <Button onClick={handleSubmit(handleSave)} color="primary" variant="primary">
        {label}
      </Button>
    )
  )
}
